/**
 * FILE: assets/cascade-picker.js
 *
 * INSTALLATION:
 *   1. Upload this file to your theme's Assets folder.
 *   2. In your theme.liquid (or the product section layout), add:
 *        {{ 'cascade-picker.js' | asset_url | script_tag }}
 *      Place it AFTER the closing </body> tag, or add defer/async.
 *      Alternatively, use Shopify's asset loading:
 *        <script src="{{ 'cascade-picker.js' | asset_url }}" defer></script>
 *
 * HOW IT WORKS:
 *   - Reads variant data injected by cascade-picker.liquid
 *   - Builds a 3-tier cascading button picker (Model → Generation → Sub-option)
 *   - Tier 3 only appears when the selected generation has more than one sub-option
 *   - On complete selection, drives the native Ascent <high-variant-selects>
 *     inputs silently so price, images, and cart keep working without modification
 */

(function () {
  'use strict';

  // Label shown above the third tier whenever it's displayed.
  const TIER3_LABEL = 'Options';

  // ============================================================
  // MAIN CLASS
  // ============================================================
  class CascadePicker {
    constructor(mountEl, data) {
      this.mount     = mountEl;
      this.sectionId = data.sectionId;
      this.variants  = data.variants;
      this.options   = data.options; // Shopify options_with_values
      this.mediaOrder = data.mediaOrder || []; // product.media ids, natural order
      this.comingSoon = data.comingSoon || {}; // { variantId: true } → coming soon (vs out of stock)
      this.productTitle = data.productTitle || ''; // for demand-tracking events

      // Current selections — null means "not yet chosen"
      this.selected = {
        model:      null,
        generation: null,
        subOption:  null,
      };

      // The resolved Shopify variant object (set when all 3 tiers are chosen)
      this.activeVariant = null;

      // Cached reference to the native <high-variant-selects> element
      this.nativePicker = null;

      this.init();
    }

    // ----------------------------------------------------------
    // INIT
    // ----------------------------------------------------------
    init() {
      // Find the native picker — it's hidden by CSS but we still drive it
      this.nativePicker = document.querySelector(
        `high-variant-selects[data-section="${this.sectionId}"]`
      );

      this.render();

      // Lock ATC immediately — nothing is selected yet
      this.lockAtc();

      // Hide any conditional popup triggers until a matching option is chosen.
      this.updatePopupTriggers();

      // Sync the media gallery to the picker's initial state.
      this.setupGallery();
    }

    /** The variant id Shopify put in the URL (?variant=...), or null. */
    getUrlVariantId() {
      return new URLSearchParams(window.location.search).get('variant');
    }

    /**
     * The variant id from the customer's saved garage vehicle — but only if it
     * belongs to THIS product (the garage may hold a variant for a different
     * product). Returns null otherwise.
     */
    getGarageVariantId() {
      const garage = window.YLGarage && window.YLGarage.get && window.YLGarage.get();
      if (!garage) return null;
      // The garage holds a Pro and an OG variant; use whichever belongs to
      // THIS product (plus a legacy single-variant fallback).
      const candidates = [garage.pro, garage.og, garage.variant].filter(Boolean);
      for (let i = 0; i < candidates.length; i++) {
        if (this.variants.some(v => String(v.id) === String(candidates[i]))) return candidates[i];
      }
      return null;
    }

    /** This section's <media-gallery> element, or null. */
    getGallery() {
      return document.getElementById(`Media-Gallery-${this.sectionId}`);
    }

    /**
     * Create the "Fits your <vehicle>" badge (hidden) when the customer's saved
     * garage vehicle matches this product. Inserted before the mount so
     * render() (which clears the mount) doesn't wipe it. Visibility is then
     * driven by updateFitBadge() so it only shows while the currently selected
     * variant is actually the garage vehicle's variant.
     */
    ensureFitBadge() {
      const garage = window.YLGarage && window.YLGarage.get && window.YLGarage.get();
      if (!garage || !garage.label) return;
      this.garageFitVariant = this.getGarageVariantId();
      if (!this.garageFitVariant) return;

      if (!this.fitBadge) {
        const badge = document.createElement('div');
        badge.className = 'cascade-fit';
        badge.hidden = true;
        badge.innerHTML =
          '<span class="cascade-fit__check" aria-hidden="true">✓</span>' +
          ' Fits your <strong class="cascade-fit__label"></strong>';
        this.mount.parentNode.insertBefore(badge, this.mount);
        this.fitBadge = badge;
      }
      this.fitBadge.querySelector('.cascade-fit__label').textContent = garage.label;
    }

    /** Show the fit badge only while the active variant IS the garage variant. */
    updateFitBadge() {
      if (!this.fitBadge) return;
      const match = this.activeVariant && this.garageFitVariant &&
        String(this.activeVariant.id) === String(this.garageFitVariant);
      this.fitBadge.hidden = !match;
    }

    /**
     * Align the media gallery with the picker on first load.
     *
     * media-gallery.js loads after this script, so the custom element may not
     * be upgraded yet — wait for it to be defined so our calls aren't no-ops.
     *
     * Steps (in order):
     *   1. Undo Shopify's "selected variant featured media first" promotion so
     *      the photo order is the natural product-media order in every case.
     *   2. Hide all variant/grouped media so only generic photos show.
     *   3. If the customer arrived on a specific variant (?variant= in URL),
     *      fill the picker to match and show that variant's media.
     */
    setupGallery() {
      const gallery = this.getGallery();
      if (!gallery) return;

      const apply = () => {
        if (typeof gallery.restoreNaturalOrder === 'function') {
          gallery.restoreNaturalOrder(this.mediaOrder);
        }
        if (typeof gallery.resetToGeneric === 'function') {
          gallery.resetToGeneric();
        }

        // Create the fit badge (hidden) if the garage vehicle fits this product.
        this.ensureFitBadge();

        // Preselect from the URL, or fall back to the customer's saved
        // garage vehicle when it fits this product.
        const preselectId = this.getUrlVariantId() || this.getGarageVariantId();
        if (preselectId) {
          const variant = this.variants.find(
            v => String(v.id) === String(preselectId)
          );
          if (variant) this.preselectVariant(variant);
        }
        this.updateFitBadge();
      };

      if (window.customElements && customElements.get('media-gallery')) {
        apply();
      } else if (window.customElements) {
        customElements.whenDefined('media-gallery').then(apply);
      }
    }

    /**
     * Pre-fill the picker from a variant (used when the page loads with a
     * variant already chosen via ?variant= in the URL) and show its media.
     */
    preselectVariant(variant) {
      this.selected.model      = variant.option1;
      this.selected.generation = variant.option2;
      this.selected.subOption  = variant.option3;
      this.resolveVariant();
    }

    /**
     * Drive the media gallery to a variant directly, rather than relying on
     * the native change event. Needed because selecting the server-default
     * variant sets no new option values, so no change fires and the theme's
     * own gallery update never runs.
     */
    updateGalleryForVariant(variant) {
      const mediaId = variant.featured_media && variant.featured_media.id;
      if (mediaId == null) return;

      const gallery = this.getGallery();
      if (gallery && typeof gallery.updateGallery === 'function') {
        gallery.updateGallery(mediaId);
      }
    }

    // ----------------------------------------------------------
    // DATA HELPERS
    // ----------------------------------------------------------

    /** All unique Model values (option1) */
    get models() {
      return [...new Set(this.variants.map(v => v.option1))];
    }

    /** Generations (option2) valid for the selected model */
    generationsFor(model) {
      return [
        ...new Set(
          this.variants
            .filter(v => v.option1 === model)
            .map(v => v.option2)
        ),
      ];
    }

    /** Sub-options (option3) valid for the selected model + generation */
    subOptionsFor(model, generation) {
      return [
        ...new Set(
          this.variants
            .filter(v => v.option1 === model && v.option2 === generation)
            .map(v => v.option3)
        ),
      ];
    }

    /** Find a variant by all three option values */
    findVariant(model, generation, subOption) {
      return this.variants.find(
        v =>
          v.option1 === model &&
          v.option2 === generation &&
          v.option3 === subOption
      ) || null;
    }

    // ----------------------------------------------------------
    // AVAILABILITY ("coming soon")
    // A value is "available" if at least one buyable variant is
    // reachable through it. variant.available is false only when a
    // variant is inventory-tracked, out of stock, and NOT set to
    // continue selling — which is exactly how we flag "coming soon".
    // Untracked variants are always available.
    // ----------------------------------------------------------

    /** Is any variant for this model buyable? */
    modelAvailable(model) {
      return this.variants.some(v => v.option1 === model && v.available);
    }

    /** Is any variant for this model + generation buyable? */
    generationAvailable(model, generation) {
      return this.variants.some(
        v => v.option1 === model && v.option2 === generation && v.available
      );
    }

    /** Is the specific model + generation + sub-option combo buyable? */
    subOptionAvailable(model, generation, subOption) {
      const variant = this.findVariant(model, generation, subOption);
      return !!(variant && variant.available);
    }

    // ----------------------------------------------------------
    // OUT OF STOCK vs COMING SOON
    // Both are inventory-unavailable (tracked/0/deny). The variant metafield
    // custom.coming_soon (exposed via comingSoon) splits them: flagged = not
    // launched yet (coming soon); unflagged (inventory simply hit 0) = out of stock.
    // ----------------------------------------------------------

    /** Label for a set of variants behind an unavailable option value. */
    unavailableLabel(variants) {
      const unavailable = variants.filter(v => !v.available);
      if (unavailable.length && unavailable.every(v => this.comingSoon[v.id])) {
        return 'Coming soon';
      }
      return 'Out of stock';
    }
    variantsForModel(model) {
      return this.variants.filter(v => v.option1 === model);
    }
    variantsForGeneration(model, generation) {
      return this.variants.filter(v => v.option1 === model && v.option2 === generation);
    }


    // ----------------------------------------------------------
    // RENDER
    // ----------------------------------------------------------
    render() {
      this.mount.innerHTML = '';

      // --- TIER 1: Model ---
      this.mount.appendChild(
        this.buildGroup({
          tier:      'model',
          labelText: 'Model',
          values:    this.models,
          selected:  this.selected.model,
          disabled:  false,
          onClick:   (value) => this.selectModel(value),
        })
      );

      // --- TIER 2: Generation ---
      const gens = this.selected.model
        ? this.generationsFor(this.selected.model)
        : [];

      this.mount.appendChild(
        this.buildGroup({
          tier:      'generation',
          labelText: 'Generation',
          values:    gens,
          selected:  this.selected.generation,
          disabled:  !this.selected.model,
          onClick:   (value) => this.selectGeneration(value),
        })
      );

      // --- TIER 3: Sub-option (conditional) ---
      const subs = (this.selected.model && this.selected.generation)
        ? this.subOptionsFor(this.selected.model, this.selected.generation)
        : [];

      // Only show tier 3 if there are multiple sub-options
      const tier3Needed = subs.length > 1;

      const tier3Group = this.buildGroup({
        tier:      'tier3',
        labelText: TIER3_LABEL,
        values:    subs,
        selected:  this.selected.subOption,
        disabled:  false,
        isUnavailable: (s) =>
          !this.subOptionAvailable(this.selected.model, this.selected.generation, s),
        labelFor:  (s) => {
          const v = this.findVariant(this.selected.model, this.selected.generation, s);
          return this.unavailableLabel(v ? [v] : []);
        },
        onClick:   (value) => this.selectSubOption(value),
        extraClass: 'cascade-picker__group--tier3' +
                    (tier3Needed ? ' is-visible' : ''),
      });

      this.mount.appendChild(tier3Group);

      // Keep the "fits your vehicle" badge in sync with the current selection.
      this.updateFitBadge();
    }

    /**
     * Build a single picker group (label + row of buttons).
     *
     * @param {object} opts
     * @param {string}   opts.tier        - 'model' | 'generation' | 'tier3'
     * @param {string}   opts.labelText   - Human-readable label prefix
     * @param {string[]} opts.values      - Option values to render as buttons
     * @param {string}   opts.selected    - Currently selected value (or null)
     * @param {boolean}  opts.disabled    - Whether the whole group is locked
     * @param {function} opts.onClick     - Callback(value) on button click
     * @param {string}   [opts.extraClass]- Extra CSS classes on the group el
     */
    buildGroup({ tier, labelText, values, selected, disabled, onClick, extraClass, isUnavailable, labelFor }) {
      const group = document.createElement('div');
      group.className = `cascade-picker__group cascade-picker__group--${tier}`;
      if (extraClass) group.className += ` ${extraClass}`;
      group.dataset.tier = tier;

      // Label
      const label = document.createElement('div');
      label.className = 'cascade-picker__label';
      label.id = `cascade-label-${tier}-${this.sectionId}`;

      const labelName = document.createElement('span');
      labelName.textContent = `${labelText}:`;
      label.appendChild(labelName);

    //   if (selected) {
    //     const labelVal = document.createElement('span');
    //     labelVal.className = 'cascade-picker__label-value';
    //     labelVal.textContent = selected;
    //     label.appendChild(labelVal);
    //   }

      group.appendChild(label);

      // Buttons
      const optionsRow = document.createElement('div');
      optionsRow.className = 'cascade-picker__options';
      optionsRow.setAttribute('role', 'group');
      optionsRow.setAttribute('aria-labelledby', label.id);

      values.forEach(value => {
        const comingSoon =
          typeof isUnavailable === 'function' && isUnavailable(value);

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cascade-picker__btn';
        btn.dataset.value = value;

        const btnText = document.createElement('span');
        btnText.className = 'cascade-picker__btn-text';
        btnText.textContent = value;
        btn.appendChild(btnText);

        let stateLabel = '';
        if (comingSoon) {
          // Out of stock / coming soon, but still SELECTABLE so the shopper can
          // reach the "Email When Available" flow. Greyed + highlighted when chosen.
          btn.classList.add('is-coming-soon');
          if (value === selected) btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', value === selected ? 'true' : 'false');

          stateLabel = (typeof labelFor === 'function' ? labelFor(value) : 'Coming soon');
          if (stateLabel === 'Out of stock') btn.classList.add('is-oos');
        } else if (disabled) {
          btn.classList.add('is-disabled');
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        } else if (value === selected) {
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
        } else {
          btn.setAttribute('aria-pressed', 'false');
        }

        btn.addEventListener('click', () => {
          // `disabled` = tier locked (earlier tier not chosen yet). Coming-soon
          // values are clickable so an out-of-stock variant can be selected.
          if (!disabled) onClick(value);
        });

        // Wrap the button so its "Coming soon"/"Out of stock" caption can sit
        // BELOW the button rather than inside it.
        const opt = document.createElement('div');
        opt.className = 'cascade-picker__opt';
        opt.appendChild(btn);
        if (comingSoon) {
          const soon = document.createElement('span');
          soon.className = 'cascade-picker__soon';
          soon.textContent = stateLabel;
          opt.appendChild(soon);
        }
        optionsRow.appendChild(opt);
      });

      group.appendChild(optionsRow);

      // Dropdown equivalent — shown on mobile (<=749px), hidden on desktop.
      // Renders the same values/state and calls the same onClick handler, so
      // whichever control the viewport shows, selection stays in sync.
      group.appendChild(
        this.buildSelect({ tier, labelText, values, selected, disabled, onClick, isUnavailable, labelFor, labelId: label.id })
      );

      return group;
    }

    /**
     * Build the mobile <select> version of a picker group.
     * Mirrors buildGroup's button logic: placeholder when nothing is chosen,
     * coming-soon values rendered as disabled options, whole control disabled
     * when the tier is locked.
     */
    buildSelect({ tier, labelText, values, selected, disabled, onClick, isUnavailable, labelFor, labelId }) {
      const wrap = document.createElement('div');
      wrap.className = 'cascade-picker__select-wrap';

      const select = document.createElement('select');
      select.className = 'cascade-picker__select';
      select.setAttribute('aria-labelledby', labelId);
      if (disabled) select.disabled = true;

      // Placeholder — visible until the user picks a value for this tier
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = `Select ${labelText}`;
      placeholder.disabled = true;
      if (!selected) placeholder.selected = true;
      select.appendChild(placeholder);

      values.forEach(value => {
        const comingSoon =
          typeof isUnavailable === 'function' && isUnavailable(value);

        const opt = document.createElement('option');
        opt.value = value;
        // Unavailable values stay selectable (they route to the notify flow).
        const stateLabel = (typeof labelFor === 'function' ? labelFor(value) : 'Coming soon');
        opt.textContent = comingSoon ? `${value} — ${stateLabel}` : value;
        if (value === selected) opt.selected = true;

        select.appendChild(opt);
      });

      select.addEventListener('change', () => {
        const value = select.value;
        if (!disabled && value) onClick(value);
      });

      wrap.appendChild(select);

      // Caret icon to match the theme's native <select> styling
      const caret = document.createElement('span');
      caret.className = 'cascade-picker__select-caret';
      caret.setAttribute('aria-hidden', 'true');
      wrap.appendChild(caret);

      return wrap;
    }

    // ----------------------------------------------------------
    // ADD TO CART LOCK / UNLOCK
    // Targets both the main ATC button and the mini-checkout + button
    // inside the product form for this section.
    // ----------------------------------------------------------

    findAtcButtons() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return [];
      return Array.from(form.querySelectorAll('button[name="add"]'));
    }

    /**
     * Express/dynamic checkout buttons (Shop Pay, PayPal, etc.). These
     * bypass the ATC button entirely, so they must be hidden until a full
     * selection is made — otherwise a customer could one-click-buy the
     * server-default variant without ever touching the picker.
     * Ascent toggles these via the `hidden` class (see HighVariantSelects).
     */
    findDynamicCheckout() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return [];
      return Array.from(form.querySelectorAll('.dynamic-checkout'));
    }

    lockAtc(message = 'Select all options to add to cart') {
      this.findAtcButtons().forEach(btn => {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');

        // Swap text on the main ATC button only (not the mini checkout icon button)
        const textSpan = btn.querySelector('span.button-text');
        if (textSpan) {
          // Store original text so we can restore it on unlock
          if (!btn.dataset.originalText) {
            btn.dataset.originalText = textSpan.textContent.trim();
          }
          textSpan.textContent = message;
        }
      });

      // Hide express checkout while the selection is incomplete
      this.findDynamicCheckout().forEach(el => el.classList.add('hidden'));
    }

    unlockAtc() {
      this.findAtcButtons().forEach(btn => {
        btn.disabled = false;
        btn.setAttribute('aria-disabled', 'false');

        // Restore original button text
        const textSpan = btn.querySelector('span.button-text');
        if (textSpan && btn.dataset.originalText) {
          textSpan.textContent = btn.dataset.originalText;
        }
      });

      // Re-show express checkout now that a full variant is selected
      this.findDynamicCheckout().forEach(el => el.classList.remove('hidden'));
    }

    // ----------------------------------------------------------
    // CONTEXTUAL POPUP TRIGGERS
    // Finds all .product-popup elements containing a [data-cascade-trigger]
    // and shows/hides them based on whether their trigger value is present
    // in the sub-options available for the current model + generation.
    // ----------------------------------------------------------
    updatePopupTriggers() {
      const availableSubs = (this.selected.model && this.selected.generation)
        ? this.subOptionsFor(this.selected.model, this.selected.generation)
        : [];

      document.querySelectorAll('.product-popup [data-cascade-trigger]').forEach(trigger => {
        const popup = trigger.closest('.product-popup');
        if (!popup) return;

        const triggerValue = trigger.dataset.cascadeTrigger;
        const shouldShow = availableSubs.includes(triggerValue);
        popup.classList.toggle('is-visible', shouldShow);
      });
    }

    // ----------------------------------------------------------
    // SELECTION HANDLERS
    // ----------------------------------------------------------

    selectModel(model) {
      this.selected.model      = model;
      this.selected.generation = null;
      this.selected.subOption  = null;
      this.activeVariant       = null;
      this.render();
      this.lockAtc();
      this.updatePopupTriggers();
      this.emitSelection(null);
    }

    selectGeneration(generation) {
      this.selected.generation = generation;
      this.selected.subOption  = null;
      this.activeVariant       = null;

      // Always lock ATC when generation changes — a new full selection is required
      this.lockAtc();

      const subs = this.subOptionsFor(this.selected.model, generation);

      // Update popups as soon as we know what subs are available for this gen
      this.updatePopupTriggers();

      if (subs.length === 1) {
        // Only one sub-option — auto-select it (don't show tier 3), whether it's
        // buyable or coming soon. resolveVariant() then either unlocks ATC or, for
        // an out-of-stock variant, locks it and emits so the notify flow can react.
        this.selected.subOption = subs[0];
        this.resolveVariant();
      } else {
        // Multiple sub-options — render tier 3 and wait for the shopper to choose.
        this.render();
        this.emitSelection(null);
      }
    }

    selectSubOption(subOption) {
      this.selected.subOption = subOption;
      this.resolveVariant();
    }

    // ----------------------------------------------------------
    // VARIANT RESOLUTION
    // Called when we have all three selections. Finds the matching
    // Shopify variant and drives the native picker.
    // ----------------------------------------------------------
    resolveVariant() {
      const { model, generation, subOption } = this.selected;
      const variant = this.findVariant(model, generation, subOption);

      if (!variant) {
        console.warn('[CascadePicker] No variant found for:', model, generation, subOption);
        this.activeVariant = null;
        this.render();
        this.lockAtc();
        this.emitSelection(null);
        return;
      }

      this.activeVariant = variant;
      this.render();

      // Keep conditional popup triggers in sync (covers URL/garage preselect,
      // which reaches here without going through selectGeneration).
      this.updatePopupTriggers();

      // Drive the native Ascent variant picker (updates price, cart id, and —
      // when option values actually change — media via the theme's own fetch).
      this.updateNativePicker(variant);

      // Also drive the gallery directly. This covers the case where the chosen
      // variant equals the server default (no option value changes, so the
      // native change event never fires). updateGallery is guarded against
      // redundant updates, so this is a no-op when the native path already ran.
      this.updateGalleryForVariant(variant);

      // Classify availability for both the ATC state and demand tracking.
      const availability = variant.available
        ? 'available'
        : (this.comingSoon[variant.id] ? 'coming_soon' : 'out_of_stock');

      // Only enable purchase for a buyable variant. "Coming soon" variants
      // stay locked; Ascent's own fetch will also reflect the sold-out state.
      if (variant.available) {
        this.unlockAtc();
      } else {
        this.lockAtc('Coming soon');
      }

      // Demand tracking: record this completed vehicle selection (GA4 + Meta) so
      // unfillable demand can be attributed to ad campaigns. Guarded so a missing
      // or failing tracker never affects the picker.
      if (window.YLDemand && typeof window.YLDemand.track === 'function') {
        window.YLDemand.track(variant, availability, {
          model: this.selected.model,
          generation: this.selected.generation,
          option: this.selected.subOption,
          product: this.productTitle,
        });
      }
    }

    // ----------------------------------------------------------
    // NATIVE PICKER BRIDGE
    // Updates Ascent's hidden <high-variant-selects> inputs so that
    // all theme-native behavior (price, images, ATC button) still fires.
    // ----------------------------------------------------------
    updateNativePicker(variant) {
      if (!this.nativePicker) return;

      const form = document.getElementById(
        `product-form-${this.sectionId}`
      );

      // --- Update the hidden variant ID input on the product form ---
      // Belt-and-suspenders: Ascent recomputes and overwrites this after its
      // own fetch, but setting it up front means the correct variant is
      // submitted to cart even if a native control can't be located below.
      if (form) {
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) {
          idInput.value = variant.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // --- Set every native option control, then recompute ONCE ---
      // Ascent reads option values from the `selected` attribute on <option>
      // (see HighVariantSelects.updateOptions) and :checked on radios. We set
      // those directly for ALL options, then fire a single change on the
      // <high-variant-selects> element so Ascent does one section fetch for the
      // FINAL combo. Dispatching per-option (the old approach) made Ascent fetch
      // intermediate combos (new model + old generation, etc.) that resolve to
      // "Sold out"/"Unavailable" and flash on the button before the final lands.
      const optionValues = [variant.option1, variant.option2, variant.option3];
      this.options.forEach((option, i) => {
        const value = optionValues[i];
        if (value == null) return;
        this.setNativeOption(option.name, value);
      });
      this.nativePicker.dispatchEvent(new Event('change', { bubbles: true }));

      // --- Fire a global variant:changed event for any other theme listeners ---
      this.emitSelection(variant);
    }

    /**
     * Broadcast the current selection (or null when no full variant is resolved)
     * so features like "Email When Available" can react to availability.
     */
    emitSelection(variant) {
      document.dispatchEvent(
        new CustomEvent('variant:changed', {
          bubbles: true,
          detail: { variant: variant || null, sectionId: this.sectionId },
        })
      );
    }

    /**
     * Set a single native option control to `value` WITHOUT dispatching — the
     * caller fires one change afterwards. Updates the `selected` attribute on
     * the chosen <option> (how Ascent reads selects) or :checked on the radio.
     * Handles dropdown (<select name="options[Name]">) and swatch/button
     * (<input type="radio" name="Name">) renderings transparently.
     */
    setNativeOption(optionName, value) {
      // Dropdown rendering
      const select = this.nativePicker.querySelector(
        `select[name="options[${optionName}]"]`
      );
      if (select) {
        let matched = false;
        Array.from(select.options).forEach(o => {
          if (o.value === value) { o.setAttribute('selected', 'selected'); matched = true; }
          else { o.removeAttribute('selected'); }
        });
        if (matched) select.value = value;
        return;
      }

      // Button / swatch rendering (radio inputs named after the option)
      const radios = this.nativePicker.querySelectorAll(
        `input[name="${CSS.escape(optionName)}"]`
      );
      if (radios.length) {
        radios.forEach(r => { r.checked = (r.value === value); });
        return;
      }

      console.warn(
        '[CascadePicker] No native control found for option:',
        optionName, '=', value
      );
    }
  }

  // ============================================================
  // BOOT — initialize all pickers on the page
  // ============================================================
  function boot() {
    const mounts = document.querySelectorAll('.cascade-picker[data-section]');

    mounts.forEach(mount => {
      const sectionId = mount.dataset.section;
      const data = window.CascadePickerData && window.CascadePickerData[sectionId];

      if (!data) {
        console.warn('[CascadePicker] No data found for section:', sectionId);
        return;
      }

      // Prevent double-init (e.g. if script loads twice)
      if (mount.dataset.pickerInit === 'true') return;
      mount.dataset.pickerInit = 'true';

      new CascadePicker(mount, data);
    });
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();